import { parseSSE } from "@qn/providers";
import type { QueryClient } from "@tanstack/react-query";
import { create } from "zustand";
import { api, toApiError } from "./api";
import { useUI } from "./ui-store";

export interface TodoItem {
  id: string;
  content: string;
  status: "pending" | "in_progress" | "completed";
}

export interface AgentLive {
  running: boolean;
  /** Streaming text of the step in flight (persisted steps come from the transcript). */
  text: string;
  thinking: string;
  seq: number;
  pendingUser?: string;
  answeredBy?: { providerName: string; model: string };
  fallbacks: Array<{ fromModel: string; toModel: string; reason: string }>;
  toolStates: Record<string, "running" | "awaiting_approval" | "denied">;
  /** Live progress of delegated specialists, keyed by sub-agent run id. */
  subagents: Record<
    string,
    { agent: string; icon: string; task: string; state: string; detail?: string }
  >;
  tasks?: TodoItem[];
  error?: string;
  status?: string;
}

interface State {
  live: Record<string, AgentLive>;
  patch: (id: string, fn: (s: AgentLive) => AgentLive) => void;
  clear: (id: string) => void;
}

const blank = (): AgentLive => ({
  running: true,
  text: "",
  thinking: "",
  seq: 0,
  fallbacks: [],
  toolStates: {},
  subagents: {},
});

export const useAgentStore = create<State>((set) => ({
  live: {},
  patch: (id, fn) => set((s) => ({ live: { ...s.live, [id]: fn(s.live[id] ?? blank()) } })),
  clear: (id) =>
    set((s) => {
      const { [id]: _x, ...rest } = s.live;
      return { live: rest };
    }),
}));

const followers = new Map<string, AbortController>();

/** Subscribe to a conversation's run events (replays from `after`). Safe to call repeatedly. */
export async function follow(convId: string, qc: QueryClient, after = 0) {
  followers.get(convId)?.abort();
  const ac = new AbortController();
  followers.set(convId, ac);
  const { patch, clear } = useAgentStore.getState();
  patch(convId, (s) => ({ ...s, running: true }));
  let finalStatus: string | undefined;
  const refresh = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ["conversation", convId] }),
      qc.invalidateQueries({ queryKey: ["conversations"] }),
    ]);
  try {
    const res = await fetch(`/api/conversations/${convId}/agent/events?after=${after}`, {
      credentials: "same-origin",
      signal: ac.signal,
      headers: { "x-qn-client": "web" },
    });
    if (!res.ok || !res.body) throw await toApiError(res);
    for await (const m of parseSSE(res.body)) {
      let ev: Record<string, any>;
      try {
        ev = JSON.parse(m.data);
      } catch {
        continue;
      }
      const seq = Number(m.id ?? 0);
      patch(convId, (s) => ({ ...s, seq: seq || s.seq }));
      switch (ev.type) {
        case "step":
          // The previous step is persisted by now; show it from the transcript and reset the live buffer.
          if (ev.n > 1) await refresh();
          patch(convId, (s) => ({ ...s, text: "", thinking: "" }));
          break;
        case "start":
          patch(convId, (s) => ({
            ...s,
            answeredBy: { providerName: ev.providerName, model: ev.model },
            text: "",
            thinking: "",
          }));
          break;
        case "fallback":
          patch(convId, (s) => ({
            ...s,
            fallbacks: [
              ...s.fallbacks,
              { fromModel: ev.fromModel, toModel: ev.toModel, reason: ev.reason },
            ],
          }));
          break;
        case "text":
          patch(convId, (s) => ({ ...s, text: s.text + ev.delta }));
          break;
        case "thinking":
          patch(convId, (s) => ({ ...s, thinking: s.thinking + ev.delta }));
          break;
        case "tool_call":
          patch(convId, (s) => ({ ...s, toolStates: { ...s.toolStates, [ev.id]: ev.state } }));
          await refresh();
          patch(convId, (s) => ({ ...s, text: "", thinking: "" }));
          break;
        case "tool_result":
          patch(convId, (s) => {
            const { [ev.id]: _d, ...rest } = s.toolStates;
            return { ...s, toolStates: rest };
          });
          await refresh();
          break;
        case "subagent":
          patch(convId, (s) => ({
            ...s,
            subagents: {
              ...s.subagents,
              [ev.id]: {
                agent: ev.agent,
                icon: ev.icon,
                task: ev.task,
                state: ev.state,
                detail: ev.detail ?? s.subagents[ev.id]?.detail,
              },
            },
          }));
          break;
        case "artifact":
          useUI.getState().openArtifact(ev.id);
          void qc.invalidateQueries({ queryKey: ["artifact", ev.id] });
          void qc.invalidateQueries({ queryKey: ["artifacts"] });
          break;
        case "tasks":
          patch(convId, (s) => ({ ...s, tasks: ev.todos }));
          break;
        case "error":
          patch(convId, (s) => ({ ...s, error: ev.message }));
          break;
        case "done":
          finalStatus = ev.status;
          break;
      }
    }
  } catch (e) {
    if (!(e instanceof DOMException && e.name === "AbortError"))
      patch(convId, (s) => ({ ...s, error: e instanceof Error ? e.message : String(e) }));
  } finally {
    if (followers.get(convId) === ac) followers.delete(convId);
    await refresh();
    const s = useAgentStore.getState().live[convId];
    // Keep request-level errors visible (they never reach the transcript); otherwise the transcript is the truth.
    if (s?.error && !finalStatus) patch(convId, (x) => ({ ...x, running: false }));
    else clear(convId);
  }
}

export async function startAgent(
  convId: string,
  body: Record<string, unknown>,
  qc: QueryClient,
  pendingUser?: string,
) {
  const { patch } = useAgentStore.getState();
  patch(convId, () => ({ ...blank(), pendingUser }));
  try {
    await api(`/conversations/${convId}/agent`, { method: "POST", body });
  } catch (e) {
    patch(convId, (s) => ({
      ...s,
      running: false,
      error: e instanceof Error ? e.message : String(e),
    }));
    return;
  }
  await follow(convId, qc, 0);
}

export async function decide(
  convId: string,
  decisions: Record<string, "approve" | "deny">,
  always: boolean,
  qc: QueryClient,
) {
  const { patch } = useAgentStore.getState();
  const after = useAgentStore.getState().live[convId]?.seq ?? 0;
  patch(convId, (s) => ({ ...s, running: true, error: undefined }));
  try {
    await api(`/conversations/${convId}/agent/decide`, {
      method: "POST",
      body: { decisions, always },
    });
  } catch (e) {
    patch(convId, (s) => ({
      ...s,
      running: false,
      error: e instanceof Error ? e.message : String(e),
    }));
    return;
  }
  await follow(convId, qc, after);
}

export async function resume(convId: string, qc: QueryClient) {
  useAgentStore.getState().patch(convId, () => blank());
  try {
    await api(`/conversations/${convId}/agent/resume`, { method: "POST", body: {} });
  } catch (e) {
    useAgentStore.getState().patch(convId, (s) => ({
      ...s,
      running: false,
      error: e instanceof Error ? e.message : String(e),
    }));
    return;
  }
  await follow(convId, qc, 0);
}

export async function cancelRun(convId: string) {
  await api(`/conversations/${convId}/agent/cancel`, { method: "POST", body: {} }).catch(() => {});
}
